import { client } from "@/client";
import { CustomToastComponent } from "@/components/shared";
import { useMutation } from "@apollo/client/react";
import {
  Tariff,
  CreateTariffMutation,
  CreateTariffMutationVariables,
  CreateTariffDocument,
} from "@/graphql/__generated__/graphql";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

/**
 * Custom hook for creating a tariff and handling the necessary cache updates.
 * @returns {Object} - Contains the createTariffMutation function, loading state, and error state.
 */
export default function useCreateTariffMutationHook(): {
  createTariffMutation: any;
  loading: any;
  error: any;
  } {
  const { t } = useTranslation(["toasts"]);

  const [createTariffMutation, { loading, error }] = useMutation<CreateTariffMutation, CreateTariffMutationVariables>(
    CreateTariffDocument, {
      onCompleted: (data) => {
        toast(CustomToastComponent, {
          toastId: "tariff-created-success",
          data: {
            title: t("toasts.tariffcreatedsuccessfully"),
            type: "success",
          },
        });

        updateCache(data.createTariff as Tariff);
      },
    });

  /**
   * Adds reference of new Tariff to cache.
   * @param {Tariff} newTariff The new tariff to be added.
   */
  const updateCache = (newTariff: Tariff) => {
    const tariffId = newTariff.id;
    // Get the cache reference of the new Tariff
    const newTariffReference = client.cache.identify({
      __typename: "Tariff",
      id: tariffId,
    });

    // Update the cache with the new Tariff reference
    // This will add the new Tariff to the paginatedTariffs query data
    client.cache.modify({
      fields: {
        paginatedTariffs(existingPaginatedTariffs = {}) {
          const { edges = [], pageInfo } = existingPaginatedTariffs;

          // Check if the tariff already exists in the cache to avoid duplicates
          const existingTariffIds = edges.map((edge: any) => edge.node.id);
          if (existingTariffIds.includes(tariffId)) {
            return existingPaginatedTariffs;
          }

          // Add the new tariff reference to the edges field
          const newEdge = {
            __typename: "TariffEdge",
            node: { __ref: newTariffReference },
            cursor: `${tariffId}`,
          };
          return {
            ...existingPaginatedTariffs,
            edges: [newEdge, ...edges],
            pageInfo: {
              ...pageInfo,
            },
          };
        },
      },
    });
  };

  return {
    createTariffMutation,
    loading,
    error,
  };
}
